import queryString, { ParsedQuery } from 'query-string';
import { Buffer } from 'buffer';

const env = process.env.GATSBY_ENV || process.env.NODE_ENV!;

export const getUrlWithTrailingSlash = (url: string) => {
  const parsedUrl = queryString.parseUrl(url, { parseFragmentIdentifier: true });
  return queryString.stringifyUrl({ ...parsedUrl, url: parsedUrl.url.endsWith('/') ? parsedUrl.url : parsedUrl.url + '/' });
};

const absoluteUrlPattern = /^https?:\/\//i;
const demoEventHandleMap: { readonly [key in string]: string } = {
  local: 'amyplusdave',
  stage: 'amyplusdave',
  development: 'amyplusdave',
  production: 'example',
};

const DEMO_EVENT_HANDLE = demoEventHandleMap[env] || demoEventHandleMap['production'];
const BLANK_TEMPLATE_THEME_ID = 'blank';

export const isWithjoyDomain = (rawUrl: string): boolean => {
  try {
    const hostname = new URL(rawUrl).hostname;
    return hostname === 'withjoy.com' || hostname.endsWith('.withjoy.com');
  } catch {
    return false;
  }
};

export const isExternalUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return absoluteUrlPattern.test(url) && !isWithjoyDomain(url);
};

const MARCOM_UTM_SOURCE_MEDIUM = {
  utm_source: 'marcom',
  utm_medium: 'web',
} as const;

const UTM_KEYS = ['utm_campaign', 'utm_medium', 'utm_source'] as const;
type UtmKey = (typeof UTM_KEYS)[number];

const getDefaultUtmValue = (key: UtmKey, defaultCampaign: string): string => {
  return key === 'utm_campaign' ? defaultCampaign : MARCOM_UTM_SOURCE_MEDIUM[key];
};

/**
 * Appends UTM params to a URL with priority:
 *   1. Existing non-empty UTM on the URL wins.
 *   2. Else UTM from the current page URL (pageQueryParams) is carried forward.
 *   3. Else Marcom defaults — utm_source=marcom, utm_medium=web, utm_campaign=<defaultCampaign>.
 *
 * `defaultCampaign` is required per-surface so attribution reflects the origin
 * (e.g., "find-a-couple" for /find, "demo" for /demo, "view-demo-site" for gallery).
 *
 * No-ops for non-withjoy URLs to prevent decorating third-party domains.
 */
export const appendMarcomUtm = (rawUrl: string, defaultCampaign: string, pageQueryParams?: ParsedQuery<string | boolean>): string => {
  if (!isWithjoyDomain(rawUrl)) return rawUrl;
  try {
    const url = new URL(rawUrl);
    for (const key of UTM_KEYS) {
      if (url.searchParams.get(key)) continue;
      const fromPage = pageQueryParams?.[key];
      const pageValue = Array.isArray(fromPage) ? fromPage[0] : fromPage;
      if (typeof pageValue === 'string' && pageValue) {
        url.searchParams.set(key, pageValue);
      } else {
        url.searchParams.set(key, getDefaultUtmValue(key, defaultCampaign));
      }
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
};

const externalRootUrlsMap: { readonly [key in string]: string } = {
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

const internalRootUrlsMap: { readonly [key in string]: string } = {
  local: 'localhost:8000',
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

const blissGatewayUrlMap: { readonly [key in string]: string } = {
  local: 'https://dev.withjoy.com', // used for local development
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

/**
 * get root url for non-marcom app, based on current marcom deployment env
 */
export const EXT_APP_ROOT_URL = externalRootUrlsMap[env] || externalRootUrlsMap['production'];

/**
 * get root url for internal marcom routes, based on current marcom deployment env
 */
export const INTERNAL_ROOT_URL = internalRootUrlsMap[env] || internalRootUrlsMap['production'];

/**
 * get root url for bliss gateway endpoints
 */
export const BLISS_GATEWAY_ROOT_URL = blissGatewayUrlMap[env] || internalRootUrlsMap['production'];

/**
 * Take url path: /login, /createwedding or /wedding-website and send to appropriate internal or external root url.
 */
export function getRootUrl(urlPath: string): string {
  const externalPaths: { readonly [key in string]: boolean } = {
    '/createwedding': true,
    '/login': true,
    '/amyplusdave': true,
    '/designs': true,
    '/event/edit/card': true,
    '/cards': true,
  };

  if (externalPaths[urlPath]) {
    return EXT_APP_ROOT_URL + urlPath;
  }

  return INTERNAL_ROOT_URL + urlPath;
}

/**
 * Get url according to the env and build (useful to run e2e test local/CI)
 */
export const getRootBuildUrl = () => {
  const query = { 'feature.marcom': process.env.SHORT_SHA };
  return queryString.stringifyUrl({ url: INTERNAL_ROOT_URL, query });
};

const baseCreateWeddingUrl = getRootUrl('/createwedding');

const pageToScenarioMap: { [key in string]: SignUpScenario } = {
  'wedding-invitations': 'invitation',
};

// TODO: move all createWedding to the generic getCreateEventUrl
export const getCreateWeddingUrl = (fromPage: SignUpScenario | undefined) => {
  const resultUrl = queryString.parseUrl(baseCreateWeddingUrl);
  const queryParams = resultUrl.query;
  if (fromPage && fromPage !== 'home') {
    queryParams.scenario = pageToScenarioMap[fromPage] ?? fromPage;
  }
  return appendMarcomUtm(queryString.stringifyUrl(resultUrl), fromPage ?? 'createwedding');
};

export const getCreateEventUrl = (fromPage: MarcomPageName | undefined) => {
  const page = fromPage === 'baby-registry' ? 'createbabyregistry' : 'createwedding';
  const resultUrl = queryString.parseUrl(getRootUrl(`/${page}`));
  const queryParams = resultUrl.query;
  if (fromPage && fromPage !== 'home') {
    queryParams.scenario = pageToScenarioMap[fromPage] ?? fromPage;
  }
  return appendMarcomUtm(queryString.stringifyUrl(resultUrl), fromPage ?? page);
};

export const getCardCategoryFromVariationType = (variationType: string) => {
  const map: { readonly [key in string]: string } = {
    invite: 'invitation',
    savethedate: 'saveTheDate',
  };

  return map[variationType] || '';
};

const VARIATION_TYPE_TO_NAMED_CATEGORY_PATH: Record<'savethedate' | 'invite', string> = {
  savethedate: 'save-the-date',
  invite: 'invitation',
};

const getBase64EncodedWebsiteDesignSettings = (websiteSettings: GalleryECard['websiteSettings']) => {
  try {
    return Buffer.from(JSON.stringify(websiteSettings)).toString('base64');
  } catch (e) {
    console.error('Error converting website settings to base64', e);
  }
};

export const getGalleryCreateWeddingUrl = (
  cardDetails: GalleryECard,
  scenario: SignUpScenario,
  template: string,
  theme: string,
  font?: string,
  startWithCtaRedirectUrlPath?: string
) => {
  if ((cardDetails.variationType === 'savethedate' || cardDetails.variationType === 'invite') && cardDetails.isJoyPrintable) {
    const resultUrl = queryString.parseUrl(
      EXT_APP_ROOT_URL + `/cards/wedding/${VARIATION_TYPE_TO_NAMED_CATEGORY_PATH[cardDetails.variationType]}/product/${theme}`
    );

    resultUrl.query.scenario = scenario;
    return appendMarcomUtm(queryString.stringifyUrl(resultUrl), scenario);
  }

  const resultUrl = queryString.parseUrl(startWithCtaRedirectUrlPath ? getRootUrl(startWithCtaRedirectUrlPath) : baseCreateWeddingUrl);
  const queryParams = resultUrl.query;
  queryParams.scenario = scenario;
  queryParams.template = template;
  queryParams.theme = cardDetails.websiteSettings?.isBlankTemplate ? BLANK_TEMPLATE_THEME_ID : theme;

  if (font) {
    queryParams.font = font;
  }
  cardDetails.designThemeId && (queryParams.designThemeId = cardDetails.designThemeId);
  if (cardDetails.websiteSettings && Object.keys(cardDetails.websiteSettings).length > 0) {
    const base64EncodedWebsiteDesignSettings = getBase64EncodedWebsiteDesignSettings(cardDetails.websiteSettings);
    base64EncodedWebsiteDesignSettings && (queryParams.wds = base64EncodedWebsiteDesignSettings);
  }
  return appendMarcomUtm(queryString.stringifyUrl(resultUrl), scenario);
};

export const getGuestSiteUrl = (
  selectedVariant: GalleryECard,
  themeId: string,
  currentQueryParams: ParsedQuery<string | boolean>,
  defaultCampaign: string
) => {
  const { variationType: layout, font } = selectedVariant;
  const { colors, pageTransparency, pageShadow, photoPresentationLayout, isBlankTemplate } = selectedVariant.websiteSettings || {};
  const { artBackground, pageBackground, pageFont, artFont, linkAndButton } = colors || {};
  const resultUrl = queryString.parseUrl(EXT_APP_ROOT_URL + `/${DEMO_EVENT_HANDLE}/welcome`);
  const queryParams = resultUrl.query;
  themeId && (queryParams.theme = isBlankTemplate ? BLANK_TEMPLATE_THEME_ID : themeId);
  layout && (queryParams.layout = layout);
  font && (queryParams.font = font);
  artBackground && (queryParams.bc = artBackground);
  pageBackground && (queryParams.pc = pageBackground);
  pageFont && (queryParams.pfc = pageFont);
  artFont && (queryParams.tc = artFont);
  linkAndButton && (queryParams.lc = linkAndButton);
  pageTransparency !== undefined && (queryParams.pt = String(pageTransparency));
  pageShadow !== undefined && (queryParams.ps = String(pageShadow));
  photoPresentationLayout && (queryParams.pl = photoPresentationLayout);
  if (Object.keys(currentQueryParams).length > 0) {
    Object.keys(currentQueryParams).forEach((key) => {
      if ((UTM_KEYS as readonly string[]).includes(key)) return;
      queryParams[key] = currentQueryParams[key] as string;
    });
  }
  return appendMarcomUtm(queryString.stringifyUrl(resultUrl), defaultCampaign, currentQueryParams);
};
